/**
 * Centralized Query Keys Factory
 *
 * Single source of truth for all TanStack Query cache keys.
 * This ensures consistent cache invalidation across the app.
 *
 * Naming Convention:
 * - `all`: Base key for a resource type (used for broad invalidation)
 * - `list`: List queries (with optional filters/pagination)
 * - `detail`: Single resource by ID
 * - Nested keys for related data (e.g., `tables.versions(tableId)`)
 *
 * Usage:
 * ```ts
 * // Query
 * useQuery({ queryKey: queryKeys.tables.detail(id), ... })
 *
 * // Invalidate all tables
 * queryClient.invalidateQueries({ queryKey: queryKeys.tables.all })
 *
 * // Invalidate specific table
 * queryClient.invalidateQueries({ queryKey: queryKeys.tables.detail(id) })
 * ```
 */

/**
 * Filter parameters for list queries
 * Intentionally flexible to support various filter shapes across different endpoints.
 * Supports both simple key-value filters and complex nested filter structures.
 */
export type ListFilterParams = Record<string, unknown>;

export const queryKeys = {
  // ============================================================================
  // Data Grid (useDataGrid hook)
  // ============================================================================
  dataGrid: {
    all: ['dataGrid'] as const,
    tables: ['dataGrid', 'tables'] as const,
    records: ['dataGrid', 'records'] as const,
    linkedTables: ['dataGrid', 'linked-tables'] as const,
    versions: ['dataGrid', 'versions'] as const,
    workspaceMembers: ['dataGrid', 'workspace-members'] as const,
    apiKeys: ['dataGrid', 'api-keys'] as const,
    auditLogs: ['dataGrid', 'audit-logs'] as const,
  },

  // ============================================================================
  // Tables
  // ============================================================================
  tables: {
    all: ['tables'] as const,
    list: (filters?: ListFilterParams) =>
      filters ? (['tables', 'list', filters] as const) : (['tables', 'list'] as const),
    detail: (id: string) => ['tables', 'detail', id] as const,
    detailDisabled: ['tables', 'detail', 'disabled'] as const,
    versions: (tableId: string) => ['tables', tableId, 'versions'] as const,
    versionsDisabled: ['tables', 'versions', 'disabled'] as const,
    fields: (tableId: string) => ['tables', tableId, 'fields'] as const,
    diff: (tableId: string, fromId: string, toId: string) =>
      ['tables', tableId, 'diff', fromId, toId] as const,
    diffDisabled: ['tables', 'diff', 'disabled'] as const,
    // Table operations (type conversions, etc.)
    operations: {
      all: (tableId: string) => ['tables', tableId, 'operations'] as const,
      list: (tableId: string, filters?: ListFilterParams) =>
        filters
          ? (['tables', tableId, 'operations', 'list', filters] as const)
          : (['tables', tableId, 'operations', 'list'] as const),
      recent: (tableId: string) => ['tables', tableId, 'operations', 'recent'] as const,
      recentDisabled: ['tables', 'operations', 'recent', 'disabled'] as const,
      active: (tableId: string) => ['tables', tableId, 'operations', 'active'] as const,
      activeDisabled: ['tables', 'operations', 'active', 'disabled'] as const,
      detail: (tableId: string, operationId: string) =>
        ['tables', tableId, 'operations', 'detail', operationId] as const,
      detailDisabled: ['tables', 'operations', 'detail', 'disabled'] as const,
      // Operation logs (non-validation events)
      logs: (tableId: string, operationId: string) =>
        ['tables', tableId, 'operations', operationId, 'logs'] as const,
      // Operation events (unified logs + errors)
      events: (tableId: string, operationId: string, params?: ListFilterParams) =>
        params
          ? (['tables', tableId, 'operations', operationId, 'events', params] as const)
          : (['tables', tableId, 'operations', operationId, 'events'] as const),
      eventsDisabled: (params?: ListFilterParams) =>
        params
          ? (['tables', 'operations', 'events', 'disabled', params] as const)
          : (['tables', 'operations', 'events', 'disabled'] as const),
      eventsSummary: (tableId: string, operationId: string) =>
        ['tables', tableId, 'operations', operationId, 'events', 'summary'] as const,
      eventsSummaryDisabled: ['tables', 'operations', 'events', 'summary', 'disabled'] as const,
    },
  },

  // ============================================================================
  // Linked Tables
  // ============================================================================
  linkedTables: {
    all: ['linkedTables'] as const,
    list: (filters?: ListFilterParams) =>
      filters ? (['linkedTables', 'list', filters] as const) : (['linkedTables', 'list'] as const),
    detail: (id: string) => ['linkedTables', 'detail', id] as const,
    preview: (id: string, params?: ListFilterParams) =>
      params
        ? (['linkedTables', id, 'preview', params] as const)
        : (['linkedTables', id, 'preview'] as const),
    fieldValues: (
      linkedTableId: string,
      fieldId: string,
      versionSelections?: Record<string, string>
    ) =>
      versionSelections
        ? (['linkedTables', linkedTableId, 'field-values', fieldId, versionSelections] as const)
        : (['linkedTables', linkedTableId, 'field-values', fieldId] as const),
    joins: {
      all: (linkedTableId: string) => ['linkedTables', linkedTableId, 'joins'] as const,
      detail: (linkedTableId: string, joinId: string) =>
        ['linkedTables', linkedTableId, 'joins', joinId] as const,
    },
    targetFields: (targetId: string, targetType: string) =>
      ['linkedTables', 'target-fields', targetId, targetType] as const,
  },

  // ============================================================================
  // Versions
  // ============================================================================
  versions: {
    all: ['versions'] as const,
    list: (filters?: ListFilterParams) =>
      filters ? (['versions', 'list', filters] as const) : (['versions', 'list'] as const),
    detail: (id: string) => ['versions', 'detail', id] as const,
    // Note: Version errors/logs have been moved to tables.operations.errors/logs
  },

  // ============================================================================
  // Workspace
  // ============================================================================
  workspace: {
    current: (workspaceId: string) => ['workspace', workspaceId] as const,
    detailsBase: ['workspace', 'details'] as const,
    details: (workspaceId: string) => ['workspace', 'details', workspaceId] as const,
    subscription: (workspaceId: string) => ['workspace', workspaceId, 'subscription'] as const,
    members: (workspaceId: string) => ['workspace', workspaceId, 'members'] as const,
    invitations: (workspaceId: string) => ['workspace', workspaceId, 'invitations'] as const,
    membershipBase: ['workspace', 'membership'] as const,
    membership: (workspaceId: string) => ['workspace', 'membership', workspaceId] as const,
    notificationPreferences: ['workspace', 'notification-preferences'] as const,
  },

  // ============================================================================
  // Workspaces
  // ============================================================================
  workspaces: {
    all: ['workspaces'] as const,
    resolver: ['workspaces', 'resolver'] as const,
  },

  // ============================================================================
  // Security / API Keys
  // ============================================================================
  security: {
    apiTokens: ['security', 'api-tokens'] as const,
    publicApiKey: ['security', 'public-api-key'] as const,
  },

  // ============================================================================
  // Billing
  // ============================================================================
  billing: {
    plans: ['billing', 'plans'] as const,
  },

  // ============================================================================
  // Organization - Data Workspace OS (folders, tags, favorites, trash)
  // ============================================================================
  organization: {
    // Folders
    folders: {
      all: ['organization', 'folders'] as const,
      list: (filters?: ListFilterParams) =>
        filters
          ? (['organization', 'folders', 'list', filters] as const)
          : (['organization', 'folders', 'list'] as const),
      tree: ['organization', 'folders', 'tree'] as const,
      detail: (id: string) => ['organization', 'folders', 'detail', id] as const,
      trash: ['organization', 'folders', 'trash'] as const,
    },
    // Tags
    tags: {
      all: ['organization', 'tags'] as const,
      list: (filters?: ListFilterParams) =>
        filters
          ? (['organization', 'tags', 'list', filters] as const)
          : (['organization', 'tags', 'list'] as const),
      detail: (id: string) => ['organization', 'tags', 'detail', id] as const,
      detailDisabled: ['organization', 'tags', 'detail', 'disabled'] as const,
      forVersion: (versionId: string) => ['organization', 'tags', 'version', versionId] as const,
      forVersionDisabled: ['organization', 'tags', 'version', 'disabled'] as const,
    },
    // Workspace items (combined view)
    items: {
      all: ['organization', 'items'] as const,
      inFolder: (folderId: string | null) =>
        ['organization', 'items', 'folder', folderId ?? 'root'] as const,
      favorites: ['organization', 'items', 'favorites'] as const,
      recent: ['organization', 'items', 'recent'] as const,
    },
  },


  // ============================================================================
  // Connectors
  // ============================================================================
  connectors: {
    all: ['connectors'] as const,
    list: ['connectors', 'list'] as const,
    detail: (tableId: string) => ['connectors', 'detail', tableId] as const,
    syncStatus: (tableId: string) => ['connectors', 'detail', tableId, 'sync-status'] as const,
    workspaceConnections: ['connectors', 'workspace-connections'] as const,
    nangoIntegrations: ['connectors', 'nango-integrations'] as const,
    connectionSyncs: (connectionId: string) => ['connectors', 'connection-syncs', connectionId] as const,
    integrationConnections: ['connectors', 'integration-connections'] as const,
    syncProgress: (tableId: string) => ['connectors', 'sync-progress', tableId] as const,
  },

  // ============================================================================
  // Onboarding
  // ============================================================================
  onboarding: {
    steps: ['onboarding', 'steps'] as const,
  },

} as const;

// Type helpers for query key inference
export type QueryKeys = typeof queryKeys;
